import { useState } from "react";
import * as Sentry from "@sentry/react";

import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import toast from "components/ui/toast/Toast";
import { BidResponsesRequest } from "lib/generated/app-service-gql/graphql";
import { useBidResponseExport } from "lib/hooks/api/bid/bid_responses/useBidResponseExport";
import { useFetchAllBidResponsesForExport } from "lib/hooks/api/bid/bid_responses/useBidResponses";
import { useFileExportStatus } from "lib/hooks/api/exports/useFileExportStatus";
import { useSubscription } from "lib/hooks/subscription";
import { IconProps } from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { Format } from "lib/types/graphQLEnums";
import { downloadFile, downloadFileFromLink, generateFilename } from "lib/utils";
import { getCombinedDocxBlob } from "./bidResponseDocxExport";

type Props = {
  filters: BidResponsesRequest;
  totalCount: number;
  buttonIcon?: IconProps["icon"];
  bidName?: string;
};
export function ExportBidResponsesButton({ filters, totalCount, buttonIcon, bidName }: Props) {
  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();
  const fetchAllBidResponses = useFetchAllBidResponsesForExport();

  const [isExportingWord, setIsExportingWord] = useState(false);

  const {
    mutate: exportData,
    isLoading,
    data,
    variables,
    reset,
  } = useBidResponseExport({
    onError: () => toast.error("Failed to start export. Please try again later."),
  });
  const { logEvent } = useTracking();
  const { data: exportStatus } = useFileExportStatus({
    exportId: data?.id,
    onSuccess(downloadUrl) {
      logEvent(EventNames.exportedData, {
        Count: totalCount,
        "Data type": EventDataTypes.bidResponse,
        Format: variables?.exportFormat?.fileFormat,
      });
      downloadFileFromLink(
        downloadUrl,
        // Filename below is used as fallback if content-disposition header is not present for some reason
        `stotles-export-${new Date().toJSON().slice(0, 10)}.${variables?.exportFormat?.fileFormat}`,
      );
      reset();
    },
    onError() {
      toast.error("Export failed. Please try again later.");
      reset();
    },
  });

  // isExporting is true if the export mutation is loading or we have an export id and exportStatus is not complete or errored
  const isExporting =
    isLoading ||
    isExportingWord ||
    !!(data?.id && !exportStatus?.fileExport.isComplete && !exportStatus?.fileExport.isError);

  const checkAccess = (): boolean => {
    if (!subscription.hasDataTypes("BIDS")) {
      openPaywallModal("BID_RESPONSE_EXPORTS", { renderMedia: false });
      return false;
    }

    return true;
  };

  const startExport = async (format: Format) => {
    if (!checkAccess()) return;
    exportData({ filters, exportFormat: { fileFormat: format } });
  };

  const handleExportWord = async () => {
    if (!checkAccess()) return;
    try {
      setIsExportingWord(true);
      const { items: responses, isTruncated } = await fetchAllBidResponses(filters);

      if (responses.length === 0) {
        toast.error("No bid responses to export.");
        return;
      }

      if (isTruncated) {
        toast.warning("Export limited to the first 500 responses.");
      }

      const blob = await getCombinedDocxBlob(responses, bidName);
      const filename = generateFilename(bidName || "bid-responses", "docx");
      downloadFile(blob, filename);

      logEvent(EventNames.exportedData, {
        Count: responses.length,
        "Data type": EventDataTypes.bidResponse,
        Format: "docx",
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "combined-docx-export" },
      });
      toast.error("Failed to export Word document. Please try again.");
    } finally {
      setIsExportingWord(false);
    }
  };

  return (
    <DropdownMenu
      width={200}
      modal={false}
      trigger={
        <Button
          buttonType="primary"
          icon={buttonIcon}
          loading={isLoading || isExporting}
          disabled={isLoading || isExporting}
        >
          {filters.ids && filters.ids.length > 0 ? "Export" : "Export all"}
        </Button>
      }
    >
      <DropdownMenuItem onClick={handleExportWord}>Export to Word (.docx)</DropdownMenuItem>
      <DropdownMenuItem onClick={() => startExport(Format.Xlsx)}>Export to XLSX</DropdownMenuItem>
      <DropdownMenuItem onClick={() => startExport(Format.Csv)}>Export to CSV</DropdownMenuItem>
    </DropdownMenu>
  );
}
